import { CreatePromptModelRequest, ModelType, UnprocessableEntityErrorResponse } from 'api/model';
import { getSchema, uiSchema } from 'api/prompts-models/formSchema';
import useModels from 'api/prompts-models/useModels';
import useSecrets from 'api/secrets/useSecrets';
import { isAxiosError } from 'axios';
import FormBlock from 'components/form/FormBlock';
import FormButtons from 'components/form/FormButtons';
import { SettingsFormShell } from 'components/settingsHub/SettingsFormShell';
import { useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

const FORM_ID = 'model-add-form';

type Props = {
  onClose: () => void;
};

const ModelAdd = ({ onClose }: Props) => {
  const { projectId } = useParams();
  const { create } = useModels();
  const { secrets } = useSecrets(projectId, true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<ModelType>();
  const [selectedBaseUrl, setSelectedBaseUrl] = useState<string>('');

  const onSubmitHandle = async (formData: CreatePromptModelRequest) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    try {
      await create.mutateAsync(formData);
      onClose();
    } catch (error) {
      if (isAxiosError<UnprocessableEntityErrorResponse>(error)) {
        const errorMessage = error.response?.data?.detail?.[0].msg;
        if (errorMessage && errorMessage.length > 0) {
          toast.error(errorMessage);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsFormShell
      title="Add Model"
      footer={<FormButtons formId={FORM_ID} onCancel={onClose} cancelBtnTxt="back to list" isLoading={isLoading} />}
    >
      <FormBlock
        isLoading={isLoading}
        formId={FORM_ID}
        showButtons={false}
        schema={getSchema(secrets)}
        uiSchema={uiSchema}
        onSubmit={(formData) => void onSubmitHandle(formData)}
        onCancel={onClose}
        onChange={(formData: CreatePromptModelRequest) => {
          const newProvider = formData.type;
          if (newProvider !== selectedProvider) {
            // Reset model in settings
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { model, ...restSettings } = formData.settings ?? {};
            formData.settings = restSettings as CreatePromptModelRequest['settings'];

            setSelectedProvider(newProvider);
          }
          const newBaseUrl = (formData.settings as { base_url?: string } | null)?.base_url ?? '';
          if (newBaseUrl !== selectedBaseUrl) {
            setSelectedBaseUrl(newBaseUrl);
          }
        }}
        formContext={{
          secrets,
          extra: {
            provider: selectedProvider ?? 'openai',
            base_url: selectedBaseUrl,
          },
        }}
      ></FormBlock>
    </SettingsFormShell>
  );
};

export default ModelAdd;
